/**
 * useCopyToClipboard — copy text and flash a "copied" flag that auto-resets.
 *
 * Replaces the copy-then-`setTimeout(setCopied(false), …)` block that was
 * duplicated across ResultColumn, ArtifactCard and CodePatchFixView. The reset
 * timer is cleared on unmount so it never fires on a gone component.
 */
import { useCallback, useEffect, useRef, useState } from "react";

export function useCopyToClipboard(resetMs = 1200): {
  copied: boolean;
  copy: (text: string) => Promise<void>;
} {
  const [copied, setCopied] = useState(false);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(
    () => () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    },
    [],
  );

  const copy = useCallback(
    async (text: string) => {
      try {
        await navigator.clipboard.writeText(text);
        setCopied(true);
        if (timerRef.current) clearTimeout(timerRef.current);
        timerRef.current = setTimeout(() => setCopied(false), resetMs);
      } catch {
        // Clipboard can reject (permissions / insecure context) — leave the
        // flag false; callers that need to surface it can check `copied`.
      }
    },
    [resetMs],
  );

  return { copied, copy };
}
